import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import API_BASE_URL from '../../config';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../Styles/Admin/Gallery.css';
import '../../axios_overrite';

export default function Gallery() {
  const [tab, setTab] = useState(0);
  const [gallery, setGallery] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editGallery, setEditGallery] = useState(null);
  const [galleryName, setGalleryName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGalleryId, setSelectedGalleryId] = useState('');
  const [imageFiles, setImageFiles] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState('gallery');
  const [editImageModalOpen, setEditImageModalOpen] = useState(false);
  const [editImage, setEditImage] = useState(null);
  const [editImageGalleryId, setEditImageGalleryId] = useState('');
  const [editImageFile, setEditImageFile] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/gallery`);
      setGallery(res.data);
    } catch (err) {
      toast.error('Failed to fetch gallery data!');
    }
  };

  const openModal = (item = null) => {
    setEditGallery(item);
    setGalleryName(item ? item.name : '');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditGallery(null);
    setGalleryName('');
  };

  const handleSave = async () => {
    try {
      if (editGallery) {
        await axios.post(`${API_BASE_URL}/api/gallery/edit/${editGallery.id}`, { name: galleryName });
        toast.success('Gallery updated successfully!');
      } else {
        await axios.post(`${API_BASE_URL}/api/gallery`, { name: galleryName });
        toast.success('Gallery added successfully!');
      }
      fetchGallery();
      closeModal();
    } catch (err) {
      toast.error('Something went wrong!');
    }
  };

  const handleDeleteGalleryModal = (id) => {
    setDeleteType('gallery');
    setDeleteTarget(id);
    setDeleteModalOpen(true);
  };

  const handleDeleteImageModal = (id) => {
    setDeleteType('image');
    setDeleteTarget(id);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (deleteType === 'gallery') {
        await axios.post(`${API_BASE_URL}/api/gallery/delete/${deleteTarget}`);
        toast.success('Gallery deleted successfully!');
      } else {
        await axios.post(`${API_BASE_URL}/api/gallery/image/delete/${deleteTarget}`);
        toast.success('Image deleted!');
      }
      fetchGallery();
    } catch (err) {
      toast.error('Delete failed!');
    }
    setDeleteModalOpen(false);
    setDeleteTarget(null);
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();
    if (!selectedGalleryId || !imageFiles.length) {
      toast.error('Please select a gallery and at least one image!');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('gallery_id', selectedGalleryId);
      for (let i = 0; i < imageFiles.length; i++) {
        formData.append('images', imageFiles[i]);
      }
      await axios.post(`${API_BASE_URL}/api/gallery/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Images uploaded successfully!');
      fetchGallery();
      setImageFiles([]);
      setSelectedGalleryId('');
    } catch (err) {
      toast.error('Image upload failed!');
    }
  };

  const openImageEditModal = (img) => {
    setEditImage(img);
    setEditImageGalleryId(img.gallery_id);
    setEditImageFile(null);
    setEditImageModalOpen(true);
  };

  const closeImageEditModal = () => {
    setEditImage(null);
    setEditImageGalleryId('');
    setEditImageFile(null);
    setEditImageModalOpen(false);
  };

  const handleImageEditSave = async () => {
    if (!editImage) return;
    try {
      const formData = new FormData();
      formData.append('gallery_id', editImageGalleryId);
      if (editImageFile) {
        formData.append('image', editImageFile);
      }
      await axios.post(`${API_BASE_URL}/api/gallery/image/edit/${editImage.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Image updated!');
      closeImageEditModal();
      fetchGallery();
    } catch (err) {
      toast.error('Image update failed!');
    }
  };

  const filteredGallery = gallery.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages0 = Math.ceil(filteredGallery.length / itemsPerPage);
  const paginatedGallery = filteredGallery.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const allImages = gallery.flatMap(g => (g.images || []).map(img => ({ ...img, eventName: g.name })));
  const totalPages2 = Math.ceil(allImages.length / itemsPerPage);
  const paginatedImages = allImages.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleTabChange = (index) => {
    setTab(index);
    setCurrentPage(1);
  };

  const renderPagination = (totalPages) => {
    if (totalPages <= 1) return null;
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || Math.abs(i - currentPage) <= 1) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '...') {
        pages.push('...');
      }
    }
    return (
      <div className="gallery-pagination">
        <button
          className="pg-arrow"
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          disabled={currentPage === 1}
        >‹</button>
        {pages.map((p, i) =>
          p === '...'
            ? <span key={`dots-${i}`} className="pg-dots">...</span>
            : <button
                key={p}
                className={`pg-btn${currentPage === p ? ' active' : ''}`}
                onClick={() => setCurrentPage(p)}
              >{p}</button>
        )}
        <button
          className="pg-arrow"
          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
        >›</button>
      </div>
    );
  };

  return (
    <AdminLayout
      title="Gallery Management"
      subtitle="Create and manage galleries"
    >
      <div className="gallery-container">

        {/* Tabs */}
        <div className="gallery-tabs-row">
          <div
            className={`gallery-tab-box${tab === 0 ? ' active' : ''}`}
            onClick={() => handleTabChange(0)}
          >
            <img src="/db-gl-01.png" alt="Add" className="tab-icon" />
            <span>Add New Event</span>
          </div>
          <div
            className={`gallery-tab-box${tab === 1 ? ' active' : ''}`}
            onClick={() => handleTabChange(1)}
          >
            <img src="/db-gl-02.png" alt="Upload" className="tab-icon" />
            <span>Upload Event Image</span>
          </div>
          <div
            className={`gallery-tab-box${tab === 2 ? ' active' : ''}`}
            onClick={() => handleTabChange(2)}
          >
            <img src="/db-gl-03.png" alt="Gallery" className="tab-icon" />
            <span>Manage Gallery</span>
          </div>
        </div>

        {/* ── TAB 0: Add New Event ── */}
        {tab === 0 && (
          <div className="gallery-card">
            <div className="gallery-card-toolbar">
              <div className="gallery-search-wrapper">
                <span className="gallery-search-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </span>
                <input
                  className="gallery-search-input"
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                />
              </div>
              <button className="add-event-btn" onClick={() => openModal()}>
                <img src="/db-gl-04.png" alt="Add" className="tab-icon" />
                <span>Add New Event</span>
              </button>
            </div>

            <div className="gallery-table-scroll">
              <table className="gallery-table">
                <thead>
                  <tr>
                    <th>S.NO</th>
                    <th>Event Name</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedGallery.map(item => (
                    <tr key={item.id}>
                      <td data-label="Id">{(currentPage - 1) * itemsPerPage + paginatedGallery.indexOf(item) + 1}</td>
                      <td data-label="Event Name">{item.name}</td>
                      <td data-label="Action">
                        <div className="action-cell">
                          <button className="action-edit-btn" onClick={() => openModal(item)}>
                            <img src="/db-gl-05.png" alt="Edit" className="action-icon" />
                            Edit
                          </button>
                          <button
                            className="action-delete-btn"
                            onClick={() => handleDeleteGalleryModal(item.id)}
                          >
                            <img src="/db-gl-06.png" alt="Delete" className="action-icon" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {paginatedGallery.length === 0 && (
                    <tr>
                      <td colSpan="3" style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                        No events found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {renderPagination(totalPages0)}
          </div>
        )}

        {/* ── TAB 1: Upload Event Image ── */}
        {tab === 1 && (
          <div className="gallery-upload-outer">
            <div className="gallery-upload-card">
              <h2 className="upload-card-title">Upload Events Images</h2>
              <div className="upload-field-group">
                <label className="upload-label">Select category:</label>
                <div className="upload-select-wrapper">
                  <select
                    className="upload-select"
                    value={selectedGalleryId}
                    onChange={e => setSelectedGalleryId(e.target.value)}
                  >
                    <option value="">-All-</option>
                    {gallery.map(item => (
                      <option key={item.id} value={item.id}>{item.name}</option>
                    ))}
                  </select>
                  <img src="/db-glr-07.png" alt="Dropdown" className="upload-select-arrow" />
                </div>
              </div>
              <div className="upload-field-group">
                <label className="upload-label">Select Image to Upload:</label>
                <div className="upload-file-wrapper">
                  <label className="upload-file-label" htmlFor="gallery-file-input">Choose File</label>
                  <span className="upload-file-name">
                    {imageFiles.length > 0 ? `${imageFiles.length} file(s) selected` : 'No file Choosen'}
                  </span>
                  <input
                    id="gallery-file-input"
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={e => setImageFiles(Array.from(e.target.files))}
                  />
                </div>
              </div>
              <button className="upload-submit-btn" onClick={handleImageUpload}>
                Upload image
              </button>
            </div>
          </div>
        )}

        {/* ── TAB 2: Manage Gallery ── */}
        {tab === 2 && (
          <div className="gallery-card">
            <div className="gallery-table-scroll">
              <table className="gallery-table manage-gallery-table">
                <thead>
                  <tr>
                    <th>S.NO</th>
                    <th>Images</th>
                    <th>Event Name</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedImages.length === 0 ? (
                    <tr>
                      <td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                        No images found.
                      </td>
                    </tr>
                  ) : (
                    paginatedImages.map((img, index) => (
  <tr key={img.id}>
    <td data-label="Id">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td data-label="Images">
                          <div className="manage-gallery-image-preview">
                            <img src={`${API_BASE_URL}${img.image_url}`} alt="gallery" />
                          </div>
                        </td>
                        <td data-label="Event Name">{img.eventName}</td>
                        <td data-label="Action">
                          <div className="action-cell">
                            <button className="action-edit-btn" onClick={() => openImageEditModal(img)}>
                              <img src="/db-gl-05.png" alt="Edit" className="action-btn-icon" />
                              Edit
                            </button>
                            <button className="action-delete-btn" onClick={() => handleDeleteImageModal(img.id)}>
                              <img src="/db-gl-06.png" alt="Delete" className="action-btn-icon" />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {renderPagination(totalPages2)}
          </div>
        )}

        {/* ── Modal: Add / Edit Gallery ── */}
        {modalOpen && (
          <div className="gallery-modal">
            <div className="gallery-modal-content">
              <h2>{editGallery ? 'Edit Event' : 'Edit / Add'}</h2>
              <label>Event Name</label>
              <input
                type="text"
                value={galleryName}
                onChange={e => setGalleryName(e.target.value)}
                placeholder="Enter event name"
              />
              <div className="gallery-modal-actions">
                <button className="edit-btn" onClick={handleSave}>Save</button>
                <button className="delete-btn" onClick={closeModal}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Modal: Edit Image ── */}
        {editImageModalOpen && editImage && (
          <div className="gallery-modal">
            <div className="gallery-modal-content edit-image-modal">
              <h2>Manage Gallery</h2>
              <div className="edit-image-preview-wrapper">
                <img
                  src={`${API_BASE_URL}${editImage.image_url}`}
                  alt="gallery"
                  className="edit-image-preview"
                />
              </div>
              <label className="edit-image-label">Select Event Name:</label>
              <select
                className="edit-image-select"
                value={editImageGalleryId}
                onChange={e => setEditImageGalleryId(e.target.value)}
              >
                {gallery.map(item => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
              <label className="edit-image-label">Select Image to Upload:</label>
              <input
                type="file"
                className="edit-image-file-input"
                onChange={e => setEditImageFile(e.target.files[0])}
              />
              <div className="gallery-modal-actions">
                <button className="edit-btn" onClick={handleImageEditSave}>Save</button>
                <button className="delete-btn" onClick={closeImageEditModal}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Modal: Delete Confirmation ── */}
        {deleteModalOpen && (
          <div className="gallery-modal">
            <div className="gallery-modal-content delete-modal">
              <div className="delete-icon">⚠️</div>
              <h2>Delete Information</h2>
              <div className="delete-message">This record will be deleted</div>
              <div className="gallery-modal-actions">
                <button
                  className="edit-btn"
                  style={{ background: '#e6eafd', color: '#1a275c', border: 'none' }}
                  onClick={() => setDeleteModalOpen(false)}
                >
                  No, Keep it
                </button>
                <button
                  className="delete-btn"
                  style={{ background: '#ff6600', color: '#fff', border: 'none' }}
                  onClick={confirmDelete}
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        )}

        <ToastContainer position="top-right" autoClose={2000} />
      </div>
    </AdminLayout>
  );
}